import { useEffect, useState, useCallback } from 'react';
import { Box, Radio } from '@mui/material';
import {
  GRID_CHECKBOX_SELECTION_COL_DEF,
  GridActionsCellItem,
} from '@mui/x-data-grid-pro';
import EditIcon from '@mui/icons-material/Edit';
import { useContext } from './context';
import * as api from '../../lib/api.js';
import { Table, TableCell } from '../../components/table/Table';
import { useGrowers } from '../../hooks/use-growers';
import { useAlerts } from '../../hooks/use-alerts';
import { TableStatusIcons } from '../../components/table/TableStatusIcons.jsx';
import { YearSelector } from './YearSelector.jsx';
import { useChangeDetection } from '../../hooks/use-change-detection';
import { useMapRefresh } from '../../components/google/maps/use-map-refresh.js';
import { useActions } from './actions/use-actions.jsx';
import { usePermission } from '../../hooks/use-permission.js';
import { InstanceAdjustments } from './adjustments/InstanceAdjustments.jsx';
import { InstanceSidePanel } from './side-panel/InstanceSidePanel.jsx';
import { TogglePanelButton } from '../../components/side-panel/TogglePanelButton';
import { useFields } from '../../hooks/use-fields.js';
import { useFeatureFlag } from '../../hooks/use-feature-flag.js';

export function InstancesTab({ tabs, renderSidePanel }) {
  const {
    isLoading,
    setLoading,
    tab,
    setInstances,
    instances,
    updateInstance,
    removeInstance,
    selectedInstances,
    setSelectedInstances,
    year,
    focusedInstanceId,
    setFocusedInstanceId,
    isSidePanelVisible,
    toggleSidePanel,
  } = useContext();

  const [filterModel, setFilterModel] = useState({ items: [] });
  const [instanceToAdjust, setInstanceToAdjust] = useState();
  const { selectedId: growerId } = useGrowers();
  const { showError } = useAlerts();
  const { refresh: refreshMap } = useMapRefresh();
  const { actions } = useActions();
  const showActions = usePermission('VIEW_ACTIONS');
  const removeFieldPermissions = useFeatureFlag('RemoveFieldPermissions');
  const { focusAaid, focusedAaid } = useFields();

  const { detectChangesFor, changedResource } =
    useChangeDetection(`lpk-prescriptions`);

  useEffect(() => {
    if (growerId) detectChangesFor({ growerId });
  }, [detectChangesFor, growerId]);

  useEffect(() => {
    if (changedResource) refreshMap();
  }, [changedResource, refreshMap]);

  useEffect(() => {
    if (changedResource) {
      api
        .getLpkInstance(growerId, changedResource.id)
        .then(updateInstance, removeInstance.bind(null, changedResource.id));
    }
  }, [growerId, changedResource, updateInstance, removeInstance]);

  useEffect(() => {
    if (growerId) {
      setLoading(true);

      api
        .getLpkInstances(growerId)
        .then(setInstances)
        .catch(showError)
        .finally(() => setLoading(false));
    }
  }, [growerId, setInstances, setLoading, showError]);

  const onRowClick = useCallback(
    ({ row }) => {
      if (focusedAaid && !row.fields.some((f) => f.aaid === focusedAaid)) {
        focusAaid(null);
      }
      return setFocusedInstanceId(row.id);
    },
    [focusAaid, focusedAaid, setFocusedInstanceId],
  );

  const getRowClassName = useCallback(
    (params) => (params.id === focusedInstanceId ? 'focused' : ''),
    [focusedInstanceId],
  );

  useEffect(() => {
    if (tab === 'instances') {
      renderSidePanel(
        isSidePanelVisible ? (
          <InstanceSidePanel
            close={toggleSidePanel}
            year={year}
            instance={instances.find(
              (instance) => instance.id === focusedInstanceId,
            )}
          />
        ) : null,
      );
    }
  }, [
    tab,
    toggleSidePanel,
    isSidePanelVisible,
    focusedInstanceId,
    year,
    renderSidePanel,
    instances,
  ]);

  if (tab !== 'instances') return null;

  return (
    <Box sx={{ overflow: 'auto', height: '100%' }}>
      {buildAdjustments()}
      <Table
        checkboxSelection
        disableRowSelectionOnClick
        rowSelectionModel={selectedInstances}
        onRowSelectionModelChange={setSelectedInstances}
        filterModel={filterModel}
        onFilterModelChange={setFilterModel}
        columns={buildColumns()}
        rows={instances}
        loading={isLoading}
        preActions={buildPreActions()}
        actions={actions}
        tabs={tabs}
        hideFooter={false}
        onRowClick={onRowClick}
        focusedRow={focusedInstanceId}
        getRowClassName={getRowClassName}
        initialState={{
          pinnedColumns: {
            left: [GRID_CHECKBOX_SELECTION_COL_DEF.field],
          },
          sorting: {
            sortModel: [{ field: 'createdAt', sort: 'asc' }],
          },
        }}
      />
    </Box>
  );

  function buildAdjustments() {
    if (!instanceToAdjust) return null;

    return (
      <InstanceAdjustments
        handleClose={() => setInstanceToAdjust(null)}
        instance={instanceToAdjust}
      />
    );
  }

  function buildPreActions() {
    return [
      <YearSelector key="year" />,
      <TogglePanelButton
        key="side-panel"
        active={isSidePanelVisible}
        onClick={toggleSidePanel}
      />,
    ].filter(Boolean);
  }

  function buildColumns() {
    return [
      {
        field: 'name',
        headerName: 'Instance Name',
        flex: 1.1,
        renderCell: (params) => (
          <TableCell
            renderCellParams={params}
            getCellErrors={[
              () =>
                params.row.fields.length ===
                  params.row.isContractedFullAccessCount +
                    params.row.isContractedLpkOnlyCount ||
                removeFieldPermissions
                  ? ''
                  : 'No Contract',
            ]}
          />
        ),
      },
      {
        field: 'fieldName',
        headerName: 'Field Name',
        flex: 1,
        valueGetter: ({ row }) =>
          row.fields.length > 1
            ? `Multiple (${row.fields.length})`
            : row.fields[0].name,
      },
      {
        field: 'numberOfCroppingYears',
        headerName: 'Num Cropping Years',
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        flex: 1,
      },
      {
        field: 'createdAt',
        headerName: 'Date Created',
        type: 'date',
        flex: 0.75,
        valueGetter: ({ row }) => new Date(row.createdAt),
      },
      {
        field: 'yearsToCritical',
        headerName: 'Years to Critical',
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        flex: 1,
      },
      {
        field: 'limeAvg',
        headerName: 'L Avg Lbs/Ac',
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        flex: 1,
        valueFormatter: ({ value }) =>
          value?.toLocaleString('en-US', { maximumFractionDigits: 0 }),
      },
      {
        field: 'phosphorusAvg',
        headerName: 'P Avg Lbs/Ac',
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        flex: 1,
        valueFormatter: ({ value }) =>
          value?.toLocaleString('en-US', { maximumFractionDigits: 0 }),
      },
      {
        field: 'potassiumAvg',
        headerName: 'K Avg Lbs/Ac',
        type: 'number',
        headerAlign: 'left',
        align: 'left',
        flex: 1,
        valueFormatter: ({ value }) =>
          value?.toLocaleString('en-US', { maximumFractionDigits: 0 }),
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 70,
        renderCell: ({ row }) => (
          <Box sx={{ height: '100%', display: 'flex', alignItems: 'center' }}>
            <TableStatusIcons
              status={row.status}
              statusDetails={row.statusDetails}
            />
          </Box>
        ),
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Adjust',
        width: 70,
        getActions: ({ row }) => [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Adjust"
            disabled={!/complete|fail/i.test(row.status) || !showActions}
            onClick={() => setInstanceToAdjust(row)}
          />,
        ],
      },
      {
        field: 'isApplied',
        headerName: 'Applied',
        align: 'center',
        width: 70,
        renderCell: ({ row }) => (
          <Radio
            inputProps={{ 'data-testid': 'isApplied' }}
            checked={row.isApplied}
            disabled={
              row.isApplying ||
              !/complete|fail/i.test(row.status) ||
              !showActions
            }
            onClick={(event) => {
              event.stopPropagation();
              return toggleApplied(row);
            }}
          />
        ),
      },
    ];
  }

  function toggleApplied(row) {
    const isApplied = !row.isApplied;
    updateInstance({ ...row, isApplying: true, isApplied });

    api
      .applyLpkInstance(growerId, row.id, !row.isApplied)
      .then(() => {
        updateInstance({ ...row, isApplying: false, isApplied });
      })
      .catch((e) => {
        showError(e);
        updateInstance({ ...row, isApplying: false, isApplied: !isApplied });
      });
  }
}
